/*
  Загрузка выписки - §3.10.2.

  Второй вид файла, который вы присылаете системе. От выгрузки начислений он
  отличается не форматом, а тем, что записывает: там долг, здесь деньги. Отсюда
  и главная особенность экрана - **список того, что система не взяла на себя
  разнести**; сам список живёт рядом, в `StatementReview.jsx` (разделено
  18.09.2026, когда экран перерос предел размера: загрузка бывает раз в месяц, а
  разбор спорного - каждый день).

  Разнести платёж «почти наверняка» нельзя: однажды это закроет чужой долг
  чужими деньгами, а вскроется при сверке. Поэтому спорное показывается вместе с
  тем, что было написано в выписке, и решение принимает человек.

  Списания организации в списке замечаний не появляются: они не ошибка и не
  поступление. Их число названо отдельно - пропуск, о котором не сказали,
  неотличим от потери данных.

  🔴 С 20.10.2026 файл уходит на сервер ОДИН раз и разбирается один раз
  (решение владельца 19.10.2026, `useUploadFlow`): выбор файла отправляет его,
  колонки распознаются сразу после разбора, как и прежде, а запись называет
  уже прочитанный файл номером и идёт фоновой работой с ходом. Конец - блок
  «Готово» (`StatementOutcome`).
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { grouped, rows as rowsCount } from '../api/format.js'
import ColumnMapping from './ColumnMapping.jsx'
import { Notice } from './Common.jsx'
// Список берётся напрямую, а не обёрткой из `Common`: та не пробрасывает
// `labelledBy`, и читалка экрана назвала бы поле просто «кнопкой» (правило 6в).
import Select from './Select.jsx'
import StatementOutcome from './StatementOutcome.jsx'
import StatementReview from './StatementReview.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'
import UploadHelp from './UploadHelp.jsx'

const SOURCES = {
  bank_statement: 'Банковская выписка',
  billing_registry: 'Реестр оплат из учётной системы',
}

/*
  Образец выписки: вид, в котором её отдаёт интернет-банк, - дата, сумма,
  назначение платежа, плательщик. Номер счёта стоит внутри назначения, как его и
  пишет плательщик: именно там система его и ищет.
*/
const SAMPLE = {
  file: 'поступления-образец.csv',
  headers: ['Дата', 'Сумма', 'Назначение платежа', 'Плательщик'],
  rows: [
    ['15.09.2026', '3 500,00', 'Оплата ЖКУ по л/с 20346 за август 2026', 'Веселков А. А.'],
    ['16.09.2026', '1 200,00', 'Квартплата лицевой счет 11852', 'Разважная Е. А.'],
  ],
}

export default function StatementUpload({ onLoaded }) {
  const flow = useUploadFlow('payments')
  const [preview, setPreview] = useState(null)
  const [choice, setChoice] = useState({})
  const [source, setSource] = useState('bank_statement')
  const [outcome, setOutcome] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Номер захода: новый заход пересоздаёт поле выбора без имени прежнего файла.
  const [round, setRound] = useState(0)
  // Отметка для очереди разбора: после записи выписки она перечитывает своё окно у
  // сервера (с 21.10.2026 очередь - серверная страница, экран читает её сам).
  const [reviewStamp, setReviewStamp] = useState(0)

  function reset() {
    setPreview(null)
    setChoice({})
    setOutcome(null)
    setError(null)
  }

  function choose(selected) {
    if (!selected) return
    reset()
    flow.choose(selected)
  }

  function again() {
    reset()
    flow.choose(null)
    setRound(round + 1)
  }

  // Файл прочитан - сразу распознавание колонок, как было до 20.10.2026: это
  // и есть «Проверка строк» хода загрузки.
  const { check } = flow
  const storedId = flow.stored?.storedId
  useEffect(() => {
    if (!storedId) return undefined
    let alive = true
    check(
      () => api.previewStatement({ storedId }),
      (seen) => `колонки распознаны, строк в файле: ${grouped(seen.rows_total)}`,
    )
      .then((understood) => {
        if (!alive) return
        setPreview(understood)
        setChoice(
          Object.fromEntries(understood.fields.map((item) => [item.field, item.header ?? ''])),
        )
      })
      .catch((failure) => alive && setError(failure))
    return () => {
      alive = false
    }
  }, [storedId, check])

  // Запись - фоновой работой по прочитанному файлу (20.10.2026); ответ тот же,
  // что отдавал запрос записи: сервер вызывает тот же обработчик.
  async function upload() {
    setBusy(true)
    setError(null)
    try {
      const written = await flow.write('payments', {
        column_overrides: JSON.stringify(choice),
        source,
      })
      setOutcome(written)
      setReviewStamp((value) => value + 1)
      // Номер загрузки отдаётся наверх, чтобы страница дождалась появления
      // строки в журнале, а не показала «Загрузок ещё не было» рядом с
      // сообщением о принятых поступлениях (11.09.2026).
      await onLoaded?.(written.import_run_id)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const missing = preview
    ? preview.fields.filter((item) => item.required && !choice[item.field])
    : []

  return (
    <>
      <UploadHelp
        title="Где это взять"
        where={[
          'Выписку выгружает банк, а не 1С: в интернет-банке откройте «Выписка» за нужный ' +
            'период и сохраните её таблицей - «Экспорт» → «Excel (xlsx)» или CSV. ' +
            'Подойдёт и выгрузка из 1С: «Отчёт по оплате» (раздел «ЖКХ», группа «Отчёты»).',
          'Нужны четыре вещи в строке: дата поступления, сумма, назначение платежа и ' +
            'плательщик. Лицевой счёт Арбитр ЖКХ ищет в назначении платежа сам.',
        ]}
        sample={SAMPLE}
        note={
          'Поступление, счёт которого определить однозначно нельзя, не разносится наугад: ' +
          'оно попадает в раздел «Требуют вашего разбора» - это чужие деньги на чужом долге.'
        }
      />

      <section className="section card">
        <div className="row">
          <div className="field field--l">
            <label className="field__control">
              <span className="field__label">Файл с поступлениями</span>
              <input
                key={`${round}-${flow.attempt}`}
                className="field__file"
                type="file"
                accept=".csv,.xlsx"
                disabled={busy || flow.busy}
                onChange={(e) => choose(e.target.files?.[0])}
              />
            </label>
          </div>
          <div className="field">
            <span className="field__label" id="statement-source">
              Откуда файл
            </span>
            <Select
              value={source}
              onChange={setSource}
              options={SOURCES}
              labelledBy="statement-source"
            />
          </div>
        </div>
        <span className="field__hint">
          Списания и итоги оборотов Арбитр ЖКХ пропустит сама - в деньги должников они не попадут.
        </span>
        <UploadStages flow={flow} checkHint="колонки распознаются сразу после разбора" />
        <Notice error={flow.error ?? error} />
      </section>

      {preview ? (
        <>
          <ColumnMapping
            preview={preview}
            choice={choice}
            disabled={busy}
            onChange={(field, header) => setChoice({ ...choice, [field]: header })}
          />
          <section className="section row">
            <button
              type="button"
              className="button"
              disabled={busy || missing.length > 0 || Boolean(outcome)}
              onClick={upload}
            >
              Загрузить {rowsCount(preview.rows_total)}
            </button>
            <button type="button" className="button button--quiet" disabled={busy} onClick={again}>
              Выбрать другой файл
            </button>
          </section>
        </>
      ) : null}

      {outcome ? (
        <StatementOutcome outcome={outcome} seconds={flow.written?.seconds} onAgain={again} />
      ) : null}

      <StatementReview stamp={reviewStamp} />
    </>
  )
}
